import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Chip, Collapse, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, LinearProgress, Divider, useTheme
} from '@mui/material';
import { Add, Delete, Link, CheckCircle, Warning, Inventory2, Dashboard as DashboardIcon, Receipt } from '@mui/icons-material';
import {
  getMaterialsDashboard, getOfferMaterials, savePurchase, deletePurchase,
  getInventory, addFromInventory, getInventoryDetail, addToInventoryDirect,
  getInventoryHistory, deleteInventoryItem, getAllPurchases,
  MaterialDashboardItem, OfferMaterials, MaterialPurchase, InventoryDetail, InventoryHistoryItem, PurchaseArchiveItem
} from '../../api/materials';
import { useSearchParams } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

const SectionTitle: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = BRAND }) => (
  <Typography sx={{
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color
  }}>{children}</Typography>
);

const matColor: Record<string, string> = {
  AL: '#F59E0B', PVC: '#EC4899', PE: '#8B5CF6',
  XLPE: '#3B82F6', HFFR: '#10B981', HDPE: '#06B6D4', CELIK: '#78716C'
};
const matBg: Record<string, string> = {
  AL: '#FEF3C7', PVC: '#FCE7F3', PE: '#EDE9FE',
  XLPE: '#DBEAFE', HFFR: '#D1FAE5', HDPE: '#CFFAFE', CELIK: '#F5F5F4'
};
const fmt = (n: number, d = 1) => n.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const MATERIAL_TYPES = ['AL', 'PVC', 'PE', 'XLPE', 'HFFR', 'HDPE', 'CELIK'];

const actionBtn = (BORDER: string) => ({
  width: 28, height: 28, borderRadius: '6px',
  border: `1px solid ${BORDER}`, color: '#64748b', bgcolor: '#fff',
});

// ─── Inventory Chart ──────────────────────────────────────────────────────────
const InventoryChart: React.FC<{ inventory: Record<string, number>; detail: Record<string, any> }> = ({ inventory, detail }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();
    const labels = Object.keys(inventory);
    const data = Object.values(inventory);
    if (labels.length === 0) return;
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: { labels, datasets: [{ label: 'Stok (kg)', data, backgroundColor: labels.map(m => matColor[m] || '#64748B'), borderColor: labels.map(m => matColor[m] || '#64748B'), borderWidth: 1, borderRadius: 6 }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${(ctx.parsed.y ?? 0).toLocaleString('tr-TR')} kg` } } }, scales: { x: { grid: { display: false } }, y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: (v) => `${Number(v).toLocaleString('tr-TR')} kg` } } } }
    });
    return () => { chartInstance.current?.destroy(); };
  }, [inventory]);
  const total = Object.values(inventory).reduce((a, b) => a + b, 0);
  if (Object.keys(inventory).length === 0) return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: '10px',
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
      <Inventory2 sx={{ fontSize: 28, color: 'text.disabled' }} />
      <SectionTitle color="text.secondary">Kasa Durumu</SectionTitle>
      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Kasada stok bulunmuyor</Typography>
    </Paper>
  );
  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: '10px', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <SectionTitle>📦 Kasa Durumu</SectionTitle>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.3 }}>
            Toplam {total.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} kg stok
          </Typography>
        </Box>
        <Chip label={`${Object.keys(inventory).length} çeşit`} size="small"
              sx={{ height: 22, bgcolor: BRAND, color: 'white', fontWeight: 700, fontSize: '11px', borderRadius: '6px' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.entries(inventory).map(([mat, kg]) => (
          <Box key={mat} sx={{
            px: 1.5, py: 1, bgcolor: matBg[mat] || '#f5f5f5', borderRadius: '8px',
            border: `1px solid ${matColor[mat] || '#e0e0e0'}`
          }}>
            <Chip label={mat} size="small"
                  sx={{ height: 18, bgcolor: matColor[mat] || '#64748B', color: 'white',
                    fontWeight: 700, fontSize: '10px', borderRadius: '4px', mb: 0.5 }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: matColor[mat], fontFamily: 'monospace' }}>
              {(kg / 1000).toFixed(3)} ton
            </Typography>
            {detail[mat]?.avgUnitPrice > 0 && (
              <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                Ort: ${detail[mat].avgUnitPrice.toFixed(0)}/t
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      <Box sx={{ position: 'relative', height: 160 }}><canvas ref={chartRef} /></Box>
    </Paper>
  );
};

// ─── Summary Chart ────────────────────────────────────────────────────────────
const SummaryChart: React.FC<{ items: MaterialDashboardItem[] }> = ({ items }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  useEffect(() => {
    if (!chartRef.current || items.length === 0) return;
    if (chartInstance.current) chartInstance.current.destroy();
    const linked = items.filter(i => i.overallProgress >= 99.9).length;
    const partial = items.filter(i => i.overallProgress > 0 && i.overallProgress < 99.9).length;
    const none = items.filter(i => i.overallProgress === 0).length;
    chartInstance.current = new Chart(chartRef.current, {
      type: 'doughnut',
      data: { labels: ['Tam Bağlandı', 'Kısmi', 'Bağlanmadı'], datasets: [{ data: [linked, partial, none], backgroundColor: ['#16A34A', '#F59E0B', '#EF4444'], borderWidth: 2, borderColor: 'white' }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed} teklif` } } }, cutout: '65%' }
    });
    return () => { chartInstance.current?.destroy(); };
  }, [items]);
  const total = items.length;
  const linked = items.filter(i => i.overallProgress >= 99.9).length;
  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: '10px', height: '100%' }}>
      <SectionTitle>Hammadde Bağlama Durumu</SectionTitle>
      <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.3, mb: 2 }}>
        Kabul edilen {total} tekliften {linked} tanesi tam bağlandı
      </Typography>
      <Box sx={{ position: 'relative', height: 200 }}><canvas ref={chartRef} /></Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[
          { label: 'Tam', count: items.filter(i => i.overallProgress >= 99.9).length, color: '#16A34A', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: 'Kısmi', count: items.filter(i => i.overallProgress > 0 && i.overallProgress < 99.9).length, color: '#D97706', bg: '#fffbeb', border: '#fed7aa' },
          { label: 'Bağlanmadı', count: items.filter(i => i.overallProgress === 0).length, color: '#DC2626', bg: '#fef2f2', border: '#fecaca' }
        ].map(({ label, count, color, bg, border }) => (
          <Box key={label} sx={{
            px: 1.5, py: 1, bgcolor: bg, borderRadius: '8px',
            border: `1px solid ${border}`, textAlign: 'center', minWidth: 70
          }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{count}</Typography>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color, letterSpacing: '0.05em', textTransform: 'uppercase', mt: 0.3 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const MaterialsPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [items, setItems] = useState<MaterialDashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const [selectedOffer, setSelectedOffer] = useState<MaterialDashboardItem | null>(null);
  const [offerMaterials, setOfferMaterials] = useState<OfferMaterials | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newPurchase, setNewPurchase] = useState<MaterialPurchase>({ materialType: 'AL', quantityKg: 0, unitPrice: 0, supplier: '', notes: '', purchasedAt: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [inventory, setInventory] = useState<Record<string, number>>({});
  const [inventoryDetail, setInventoryDetail] = useState<Record<string, InventoryDetail>>({});
  const [excessMessage, setExcessMessage] = useState<string | null>(null);
  const [inventoryTab, setInventoryTab] = useState<'new' | 'stock'>('new');
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [newStock, setNewStock] = useState({ materialType: 'AL', quantityKg: 0, unitPrice: 0, supplier: '', notes: '' });
  const [stockSaving, setStockSaving] = useState(false);
  const [kasaOpen, setKasaOpen] = useState(false);
  const [inventoryHistory, setInventoryHistory] = useState<InventoryHistoryItem[]>([]);
  const [pageTab, setPageTab] = useState(0);
  const [purchases, setPurchases] = useState<PurchaseArchiveItem[]>([]);
  const [filterMat, setFilterMat] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [dashboard, inv, detail, history, allPurchases] = await Promise.all([
        getMaterialsDashboard(), getInventory(), getInventoryDetail(), getInventoryHistory(), getAllPurchases()
      ]);
      setItems(dashboard); setInventory(inv); setInventoryDetail(detail);
      setInventoryHistory(history); setPurchases(allPurchases);
    } catch { setError('Veriler yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const offerId = searchParams.get('offer');
    if (!offerId || items.length === 0) return;
    const item = items.find(i => i.offerId === parseInt(offerId));
    if (item) openModal(item);
  }, [items, searchParams]); // eslint-disable-line

  const openModal = async (item: MaterialDashboardItem) => {
    setSelectedOffer(item); setModalOpen(true); setModalLoading(true);
    try { const data = await getOfferMaterials(item.offerId); setOfferMaterials(data); }
    catch { setError('Hammadde bilgisi yüklenemedi'); }
    finally { setModalLoading(false); }
  };

  const handleSave = async () => {
    if (!selectedOffer) return;
    setSaving(true);
    try {
      const result = await savePurchase(selectedOffer.offerId, newPurchase) as any;
      const data = await getOfferMaterials(selectedOffer.offerId);
      setOfferMaterials(data); setAddOpen(false);
      setNewPurchase({ materialType: 'AL', quantityKg: 0, unitPrice: 0, supplier: '', notes: '', purchasedAt: new Date().toISOString().split('T')[0] });
      if (result?.excessMessage) setExcessMessage(result.excessMessage);
      load();
    } catch { setError('Kayıt hatası'); }
    finally { setSaving(false); }
  };

  const handleAddFromInventory = async (materialType: string, quantityKg: number) => {
    if (!selectedOffer) return;
    try {
      const result = await addFromInventory(selectedOffer.offerId, materialType, quantityKg);
      const data = await getOfferMaterials(selectedOffer.offerId);
      setOfferMaterials(data); setExcessMessage(result.message);
      setAddOpen(false); setInventoryTab('new'); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Kasadan ekleme hatası'); }
  };

  const handleAddStock = async () => {
    setStockSaving(true);
    try {
      const result = await addToInventoryDirect(newStock);
      setExcessMessage(result.message); setAddStockOpen(false);
      setNewStock({ materialType: 'AL', quantityKg: 0, unitPrice: 0, supplier: '', notes: '' }); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Ekleme hatası'); }
    finally { setStockSaving(false); }
  };

  const handleDeleteInventory = async (id: number) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try { await deleteInventoryItem(id); load(); }
    catch { setError('Silme hatası'); }
  };

  const handleDelete = async (purchaseId: number) => {
    if (!window.confirm('Bu alımı silmek istediğinize emin misiniz?')) return;
    try {
      await deletePurchase(purchaseId);
      if (selectedOffer) { const data = await getOfferMaterials(selectedOffer.offerId); setOfferMaterials(data); }
      load();
    } catch { setError('Silme hatası'); }
  };

  const getProgressBg = (pct: number) => {
    const filledColor = pct >= 99.9 ? 'rgba(220,252,231,0.6)' : pct > 0 ? 'rgba(254,243,199,0.6)' : 'rgba(254,226,226,0.4)';
    return `linear-gradient(to right, ${filledColor} ${pct}%, transparent ${pct}%)`;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            Hammadde Yönetimi
          </Typography>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            Kabul edilen tekliflerin hammadde bağlama durumu
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Inventory2 sx={{ fontSize: 18 }} />} onClick={() => setKasaOpen(v => !v)}
                sx={{
                  bgcolor: kasaOpen ? '#15803d' : '#16A34A', fontWeight: 600, borderRadius: '8px',
                  px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                  '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }
                }}>
          KASA {Object.keys(inventory).length > 0 && `(${Object.values(inventory).reduce((a, b) => a + b, 0).toFixed(0)} kg)`}
        </Button>
      </Box>

      {/* ── KASA PANELİ ── */}
      <Collapse in={kasaOpen}>
        <Paper elevation={0} sx={{ mb: 3, border: '2px solid #16A34A', borderRadius: '10px', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#16A34A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                📦 Kasa
              </Typography>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>
                Mevcut hammadde stoku
              </Typography>
            </Box>
            <Button size="small" variant="contained" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => setAddStockOpen(true)}
                    sx={{ bgcolor: 'white', color: '#16A34A', borderRadius: '8px',
                      fontSize: '12px', fontWeight: 600, textTransform: 'none', boxShadow: 'none',
                      '&:hover': { bgcolor: '#f0fdf4', boxShadow: 'none' } }}>
              Ekle
            </Button>
          </Box>
          {Object.keys(inventory).length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: TEXT2 }}>
              <Typography sx={{ fontSize: '13px' }}>Kasada stok bulunmuyor</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
              {Object.entries(inventoryDetail).map(([mat, detail]) => (
                <Box key={mat} sx={{
                  px: 2, py: 1.5, bgcolor: matBg[mat] || '#f5f5f5', borderRadius: '10px',
                  border: `1px solid ${matColor[mat] || '#e0e0e0'}`, minWidth: 130
                }}>
                  <Chip label={mat} size="small"
                        sx={{ height: 20, bgcolor: matColor[mat] || '#64748B', color: 'white',
                          fontWeight: 700, fontSize: '11px', borderRadius: '6px', mb: 0.5 }} />
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, color: matColor[mat], fontFamily: 'monospace' }}>
                    {(detail.quantityKg / 1000).toFixed(3)} ton
                  </Typography>
                  {detail.avgUnitPrice > 0 && (
                    <Typography sx={{ fontSize: '10px', color: TEXT2 }}>
                      Ort: ${detail.avgUnitPrice.toFixed(0)}/ton
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` }}>
            <SectionTitle color={TEXT2}>Geçmiş Kayıtlar</SectionTitle>
          </Box>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Tarih', 'Hammadde', 'Miktar', 'Fiyat', 'Kaynak', 'Not/Teklif', ''].map(h => (
                    <TableCell key={h}
                               style={{ background: '#f8f9fc', paddingTop: 10, paddingBottom: 10,
                                 fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                 textTransform: 'uppercase', letterSpacing: '0.05em',
                                 borderBottom: '1px solid #e8eaf0' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryHistory.map(h => (
                  <TableRow key={h.id}
                            sx={{
                              transition: 'background .1s',
                              '& td': { borderBottom: '1px solid #e8eaf0', py: 0.8 },
                              '&:hover td': { bgcolor: '#fafbfe !important' },
                            }}>
                    <TableCell><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{new Date(h.createdAt).toLocaleDateString('tr-TR')}</Typography></TableCell>
                    <TableCell>
                      <Chip label={h.materialType} size="small"
                            sx={{ height: 18, bgcolor: matColor[h.materialType] || '#64748B',
                              color: 'white', fontWeight: 700, fontSize: '10px', borderRadius: '4px' }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace',
                        color: h.quantityKg > 0 ? '#16A34A' : '#EF4444' }}>
                        {h.quantityKg > 0 ? '+' : ''}{h.quantityKg.toFixed(0)} kg
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '11px', color: TEXT2, fontFamily: 'monospace' }}>
                        {h.unitPrice ? `$${h.unitPrice.toFixed(0)}/t` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={h.source} size="small" variant="outlined"
                            sx={{ height: 18, fontSize: '9px', borderRadius: '4px',
                              color: h.source === 'MANUAL' ? '#16A34A' : h.source === 'EXCESS' ? '#F59E0B' : '#64748B',
                              borderColor: h.source === 'MANUAL' ? '#16A34A' : h.source === 'EXCESS' ? '#F59E0B' : '#e0e0e0' }} />
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{h.offerOrderNo || h.notes || '—'}</Typography></TableCell>
                    <TableCell>
                      {h.source === 'MANUAL' && (
                        <IconButton size="small" onClick={() => handleDeleteInventory(h.id)}
                                    style={actionBtn(BORDER)}
                                    sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                          <Delete sx={{ fontSize: 13 }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Sayfa Sekmeleri ── */}
      <Paper elevation={0} sx={{ mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)}
              sx={{ px: 2, '& .Mui-selected': { color: BRAND }, '& .MuiTabs-indicator': { bgcolor: BRAND } }}>
          <Tab icon={<DashboardIcon sx={{ fontSize: 16 }} />} iconPosition="start"
               label="Dashboard"
               sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, minHeight: 48 }} />
          <Tab icon={<Receipt sx={{ fontSize: 16 }} />} iconPosition="start"
               label={`Alım Arşivi (${purchases.length})`}
               sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, minHeight: 48 }} />
        </Tabs>
      </Paper>

      {/* ===== TAB 0: DASHBOARD ===== */}
      {pageTab === 0 && (
        <Box>
          {/* Özet grafikler */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '0 0 300px' }}><SummaryChart items={items} /></Box>
            <Box sx={{ flex: '0 0 300px' }}><InventoryChart inventory={inventory} detail={inventoryDetail} /></Box>
            <Paper elevation={0} sx={{ p: 2.5, flex: 1, minWidth: 300, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
              <SectionTitle>Toplam Hammadde İhtiyacı</SectionTitle>
              <Box sx={{ mt: 2 }}>
              {(() => {
                const totals: Record<string, { needed: number; linked: number }> = {};
                items.forEach(item => {
                  Object.entries(item.needed).forEach(([mat, kg]) => {
                    if (!totals[mat]) totals[mat] = { needed: 0, linked: 0 };
                    totals[mat].needed += kg; totals[mat].linked += item.linked[mat] || 0;
                  });
                });
                return Object.entries(totals).map(([mat, { needed, linked }]) => (
                  <Box key={mat} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={mat} size="small"
                              sx={{ height: 22, bgcolor: matColor[mat] || '#64748B', color: 'white',
                                fontWeight: 700, fontSize: '11px', borderRadius: '6px' }} />
                        <Typography sx={{ fontSize: '12px', color: TEXT2, fontFamily: 'monospace' }}>
                          {fmt(linked / 1000, 2)} / {fmt(needed / 1000, 2)} ton
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace',
                        color: linked >= needed ? '#16A34A' : '#EF4444' }}>
                        %{fmt(needed > 0 ? Math.min(100, linked / needed * 100) : 0, 0)}
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={needed > 0 ? Math.min(100, linked / needed * 100) : 0}
                                    sx={{ height: 8, borderRadius: '4px', bgcolor: '#f0f0f0',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: linked >= needed ? '#16A34A' : matColor[mat] || '#64748B',
                                        borderRadius: '4px'
                                      } }} />
                  </Box>
                ));
              })()}
              </Box>
            </Paper>
          </Box>

          {/* Teklif tablosu */}
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', bgcolor: SURFACE }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SectionTitle>Kabul Edilen Teklifler</SectionTitle>
              <Chip label={`${items.length} teklif`} size="small"
                    sx={{ height: 22, bgcolor: BRAND, color: 'white', fontWeight: 700,
                      fontSize: '11px', borderRadius: '6px' }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Teklif No', 'Müşteri', 'Hammaddeler', 'Bağlanma Durumu', 'Durum', 'İşlem'].map((h, idx) => (
                      <TableCell key={h} align={idx >= 4 ? 'center' : 'left'}
                                 style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                   fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                   textTransform: 'uppercase', letterSpacing: '0.05em',
                                   borderBottom: '1px solid #e8eaf0',
                                   width: idx === 3 ? 200 : (idx >= 4 ? 100 : undefined) }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8, color: TEXT2 }}>
                        <Typography sx={{ fontSize: '13px' }}>Kabul edilmiş teklif bulunamadı</Typography>
                      </TableCell>
                    </TableRow>
                  ) : items.map(item => {
                    const pct = item.overallProgress; const done = pct >= 99.9; const started = pct > 0;
                    return (
                      <TableRow key={item.offerId}
                                sx={{
                                  background: getProgressBg(pct),
                                  transition: 'all 0.15s',
                                  '& td': { borderBottom: '1px solid #e8eaf0', py: 1 },
                                  '&:hover': { filter: 'brightness(0.97)' },
                                }}>
                        <TableCell>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND, fontFamily: 'monospace' }}>
                            {item.orderNo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.customerName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {Object.keys(item.needed).map(mat => (
                              <Chip key={mat} label={mat} size="small"
                                    sx={{ height: 20, bgcolor: matBg[mat] || '#f5f5f5',
                                      color: matColor[mat] || '#64748B', fontWeight: 700,
                                      fontSize: '10px', borderRadius: '4px',
                                      border: `1px solid ${matColor[mat] || '#e0e0e0'}` }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                              <Typography sx={{ fontSize: '11px', color: TEXT2 }}>
                                {Object.keys(item.linked).length > 0
                                  ? `${Object.keys(item.linked).length}/${Object.keys(item.needed).length} hammadde`
                                  : 'Henüz bağlanmadı'}
                              </Typography>
                              <Typography sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace',
                                color: done ? '#16A34A' : started ? '#D97706' : '#EF4444' }}>
                                %{fmt(pct, 0)}
                              </Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={pct}
                                            sx={{ height: 5, borderRadius: '3px', bgcolor: 'rgba(0,0,0,0.08)',
                                              '& .MuiLinearProgress-bar': {
                                                bgcolor: done ? '#16A34A' : started ? '#D97706' : '#EF4444',
                                                borderRadius: '3px'
                                              } }} />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {done ? (
                            <Chip label="Tam" size="small" icon={<CheckCircle sx={{ fontSize: '13px !important' }} />}
                                  sx={{ height: 22, bgcolor: '#f0fdf4', color: '#16A34A',
                                    fontWeight: 700, fontSize: '11px', borderRadius: '6px',
                                    border: '1px solid #bbf7d0' }} />
                          ) : started ? (
                            <Chip label="Kısmi" size="small" icon={<Warning sx={{ fontSize: '13px !important' }} />}
                                  sx={{ height: 22, bgcolor: '#fffbeb', color: '#D97706',
                                    fontWeight: 700, fontSize: '11px', borderRadius: '6px',
                                    border: '1px solid #fed7aa' }} />
                          ) : (
                            <Chip label="Bekliyor" size="small"
                                  sx={{ height: 22, bgcolor: '#fef2f2', color: '#DC2626',
                                    fontWeight: 700, fontSize: '11px', borderRadius: '6px',
                                    border: '1px solid #fecaca' }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button size="small" variant="outlined" startIcon={<Link sx={{ fontSize: 14 }} />}
                                  onClick={() => openModal(item)}
                                  sx={{ fontSize: '11px', borderRadius: '6px', textTransform: 'none',
                                    borderColor: BRAND, color: BRAND, height: 28,
                                    '&:hover': { bgcolor: '#eff6ff', borderColor: BRAND } }}>
                            Yönet
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* ===== TAB 1: ALIM ARŞİVİ ===== */}
      {pageTab === 1 && (
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', bgcolor: SURFACE }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <SectionTitle>Hammadde Alım Arşivi</SectionTitle>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5, flexWrap: 'wrap',
                       '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <TextField select size="small" label="Hammadde" value={filterMat} onChange={e => setFilterMat(e.target.value)} sx={{ minWidth: 130 }}>
                <MenuItem value="">Tümü</MenuItem>
                {MATERIAL_TYPES.map(m => (
                  <MenuItem key={m} value={m}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: matColor[m] || '#64748B' }} />{m}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Kaynak" value={filterSource} onChange={e => setFilterSource(e.target.value)} sx={{ minWidth: 105 }}>
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="Teklif">Teklif Alımı</MenuItem>
                <MenuItem value="Kasa">Kasa Alımı</MenuItem>
              </TextField>
              <TextField size="small" label="Tedarikçi" value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)} sx={{ minWidth: 150 }} placeholder="Tedarikçi ara..." />
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Tarih', 'Hammadde', 'Miktar (kg)', 'Birim Fiyat', 'Toplam', 'Tedarikçi', 'Kaynak', 'Teklif'].map(h => (
                    <TableCell key={h}
                               style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                 fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                 textTransform: 'uppercase', letterSpacing: '0.05em',
                                 borderBottom: '1px solid #e8eaf0' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases
                  .filter(p =>
                    (p.source === 'Teklif' || p.source === 'Kasa') &&
                    (!filterMat || p.materialType === filterMat) &&
                    (!filterSource || p.source === filterSource) &&
                    (!filterSupplier || (p.supplier || '').toLowerCase().includes(filterSupplier.toLowerCase()))
                  )
                  .map(p => (
                    <TableRow key={p.id}
                              sx={{
                                transition: 'background .1s',
                                '& td': { borderBottom: '1px solid #e8eaf0', py: 1 },
                                '&:hover td': { bgcolor: '#fafbfe !important' },
                              }}>
                      <TableCell><Typography sx={{ fontSize: '12px', color: TEXT2 }}>{p.purchasedAt ? new Date(p.purchasedAt).toLocaleDateString('tr-TR') : '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip label={p.materialType} size="small"
                              sx={{ height: 20, bgcolor: matColor[p.materialType] || '#64748B',
                                color: 'white', fontWeight: 700, fontSize: '11px', borderRadius: '4px' }} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                          {p.quantityKg.toFixed(0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                          {p.unitPrice > 0 ? `$${p.unitPrice.toFixed(0)}/ton` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace', color: '#16A34A' }}>
                          {p.totalPrice ? `$${p.totalPrice.toFixed(2)}` : p.unitPrice > 0 ? `$${(p.quantityKg * p.unitPrice / 1000).toFixed(2)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px', color: TEXT2 }}>{p.supplier && p.supplier !== 'KASA' ? p.supplier : '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={p.source} size="small" variant="outlined"
                              sx={{ height: 20, fontSize: '10px', borderRadius: '4px',
                                color: p.source === 'Kasadan' ? '#16A34A' : BRAND,
                                borderColor: p.source === 'Kasadan' ? '#16A34A' : BRAND }} />
                      </TableCell>
                      <TableCell>
                        {p.offerOrderNo ? (
                          <Typography sx={{
                            fontSize: '12px', fontWeight: 700, color: BRAND, fontFamily: 'monospace',
                            cursor: 'pointer', '&:hover': { textDecoration: 'underline' }
                          }}
                                      onClick={() => window.open(`/offers/${p.offerId}`, '_blank')}>
                            {p.offerOrderNo}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                {purchases.filter(p => (!filterMat || p.materialType === filterMat) && (!filterSource || p.source === filterSource) && (!filterSupplier || (p.supplier || '').toLowerCase().includes(filterSupplier.toLowerCase()))).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8, color: TEXT2 }}>
                      <Typography sx={{ fontSize: '13px' }}>Kayıt bulunamadı</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Hammadde Yönetim Modalı ── */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: BRAND, color: 'white', py: 2, px: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, fontFamily: 'monospace' }}>
                {selectedOffer?.orderNo} — Hammadde Yönetimi
              </Typography>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>
                {selectedOffer?.customerName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => setAddOpen(true)}
                      sx={{ bgcolor: ACCENT, fontSize: '12px', fontWeight: 600, borderRadius: '6px',
                        textTransform: 'none', boxShadow: 'none',
                        '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
                Alım Ekle
              </Button>
              {Object.keys(inventory).length > 0 && (
                <Button size="small" variant="outlined"
                        onClick={() => { setInventoryTab('stock'); setAddOpen(true); }}
                        sx={{ borderColor: 'white', color: 'white', fontSize: '12px',
                          fontWeight: 600, borderRadius: '6px', textTransform: 'none',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'white' } }}>
                  📦 Kasadan Ekle
                </Button>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          {excessMessage && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setExcessMessage(null)}>✅ {excessMessage}</Alert>}
          {modalLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: BRAND }} /></Box>
          : offerMaterials ? (
            <>
              <SectionTitle color={TEXT2}>Hammadde İhtiyacı</SectionTitle>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, mt: 1.5 }}>
                {Object.entries(offerMaterials.needed).map(([mat, kg]) => {
                  const linked = offerMaterials.linked[mat] || 0;
                  const pct = kg > 0 ? Math.min(100, linked / kg * 100) : 0;
                  return (
                    <Box key={mat} sx={{
                      p: 1.75, bgcolor: matBg[mat] || '#f5f5f5', borderRadius: '10px',
                      border: `1px solid ${matColor[mat] || '#e0e0e0'}`,
                      minWidth: 140, flex: '1 1 140px'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Chip label={mat} size="small"
                              sx={{ height: 20, bgcolor: matColor[mat] || '#64748B',
                                color: 'white', fontWeight: 700, fontSize: '11px', borderRadius: '4px' }} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace',
                          color: pct >= 99.9 ? '#16A34A' : '#EF4444' }}>
                          %{fmt(pct, 0)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, fontFamily: 'monospace' }}>
                        {fmt(linked / 1000, 3)} ton
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: TEXT2, fontFamily: 'monospace' }}>
                        / {fmt(kg / 1000, 3)} ton
                      </Typography>
                      <LinearProgress variant="determinate" value={pct}
                                      sx={{ mt: 0.75, height: 4, borderRadius: '3px', bgcolor: 'rgba(0,0,0,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: pct >= 99.9 ? '#16A34A' : matColor[mat], borderRadius: '3px'
                                        } }} />
                    </Box>
                  );
                })}
              </Box>
              <Divider sx={{ my: 2 }} />
              <SectionTitle color={TEXT2}>Alınan Hammaddeler ({offerMaterials.purchases.length} kayıt)</SectionTitle>
              <Box sx={{ mt: 1.5 }}>
              {offerMaterials.purchases.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', color: TEXT2, bgcolor: '#fafbfc', borderRadius: '8px' }}>
                  <Typography sx={{ fontSize: '13px' }}>Henüz hammadde alımı eklenmemiş</Typography>
                  <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => setAddOpen(true)}
                          sx={{ mt: 1, color: BRAND, textTransform: 'none', fontSize: '12px' }}>
                    İlk alımı ekle
                  </Button>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Hammadde', 'Miktar (kg)', 'Birim Fiyat', 'Toplam', 'Tedarikçi', 'Tarih', ''].map(h => (
                        <TableCell key={h}
                                   style={{ background: '#f8f9fc', paddingTop: 10, paddingBottom: 10,
                                     fontSize: 10, fontWeight: 600, color: '#8a92a6',
                                     textTransform: 'uppercase', letterSpacing: '0.05em',
                                     borderBottom: '1px solid #e8eaf0' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offerMaterials.purchases.map(p => (
                      <TableRow key={p.id}
                                sx={{
                                  '& td': { borderBottom: '1px solid #e8eaf0', py: 0.8 },
                                  '&:hover td': { bgcolor: '#fafbfe !important' },
                                }}>
                        <TableCell>
                          <Chip label={p.materialType} size="small"
                                sx={{ height: 20, bgcolor: matColor[p.materialType] || '#64748B',
                                  color: 'white', fontWeight: 700, fontSize: '11px', borderRadius: '4px' }} />
                        </TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(p.quantityKg, 0)}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>${fmt(p.unitPrice, 0)}/ton</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace', color: '#16A34A' }}>${fmt(p.quantityKg * p.unitPrice / 1000, 2)}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{p.supplier || '—'}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{p.purchasedAt || '—'}</Typography></TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => p.id && handleDelete(p.id)}
                                      style={actionBtn(BORDER)}
                                      sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                            <Delete sx={{ fontSize: 13 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              </Box>
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '10px', border: `1px solid ${BORDER}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase', color: TEXT2 }}>
                    Genel Tamamlanma
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, fontFamily: 'monospace',
                    color: offerMaterials.overallProgress >= 99.9 ? '#16A34A' : '#D97706' }}>
                    %{fmt(offerMaterials.overallProgress, 1)}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={offerMaterials.overallProgress}
                                sx={{ height: 10, borderRadius: '5px', bgcolor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: offerMaterials.overallProgress >= 99.9 ? '#16A34A' : '#D97706',
                                    borderRadius: '5px'
                                  } }} />
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setModalOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Alım Ekleme Modalı ── */}
      <Dialog open={addOpen} onClose={() => { setAddOpen(false); setInventoryTab('new'); }} maxWidth="sm" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: BRAND, color: 'white', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
          {inventoryTab === 'new' ? 'Hammadde Alımı Ekle' : 'Kasadan Hammadde Ekle'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          {inventoryTab === 'stock' && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '13px', color: TEXT2, mb: 2 }}>
                Kasadaki hammaddelerden bu teklife bağlayın
              </Typography>
              {Object.entries(inventory).filter(([mat]) => offerMaterials?.needed[mat] && offerMaterials.needed[mat] > 0).length === 0 && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Bu teklifin kasadaki hammaddelerle eşleşen ihtiyacı bulunmuyor.
                </Alert>
              )}
              {Object.entries(inventory).filter(([mat]) => offerMaterials?.needed[mat] && offerMaterials.needed[mat] > 0).map(([mat, kg]) => (
                <Box key={mat} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, mb: 1.5,
                  p: 1.5, bgcolor: matBg[mat] || '#f5f5f5', borderRadius: '8px'
                }}>
                  <Chip label={mat} size="small"
                        sx={{ height: 22, bgcolor: matColor[mat] || '#64748B',
                          color: 'white', fontWeight: 700, fontSize: '11px',
                          borderRadius: '6px', minWidth: 50 }} />
                  <Typography sx={{ fontSize: '12px', flex: 1 }}>
                    Mevcut: <Box component="strong" sx={{ fontFamily: 'monospace' }}>{kg.toFixed(0)} kg</Box>
                  </Typography>
                  <TextField type="number" size="small" label="Miktar (kg)" defaultValue={0} id={`inv-${mat}`}
                             inputProps={{ min: 0, max: kg, step: 100 }} sx={{ width: 130 }} />
                  <Button size="small" variant="contained"
                          sx={{ bgcolor: '#16A34A', borderRadius: '6px', textTransform: 'none',
                            fontSize: '12px', boxShadow: 'none',
                            '&:hover': { bgcolor: '#15803d', boxShadow: 'none' } }}
                          onClick={() => {
                            const el = document.getElementById(`inv-${mat}`) as HTMLInputElement;
                            const qty = parseFloat(el?.value || '0');
                            if (qty > 0) handleAddFromInventory(mat, qty);
                          }}>
                    Ekle
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          {inventoryTab === 'new' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField select label="Hammadde Tipi" value={newPurchase.materialType} size="small"
                         onChange={e => setNewPurchase({ ...newPurchase, materialType: e.target.value })} fullWidth>
                {MATERIAL_TYPES.map(m => (
                  <MenuItem key={m} value={m}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: matColor[m] || '#64748B' }} />{m}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Miktar (kg)" type="number" size="small" value={newPurchase.quantityKg || ''}
                           onChange={e => setNewPurchase({ ...newPurchase, quantityKg: parseFloat(e.target.value) || 0 })}
                           fullWidth inputProps={{ min: 0, step: 100 }} />
                <TextField label="Birim Fiyat ($/ton)" type="number" size="small" value={newPurchase.unitPrice || ''}
                           onChange={e => setNewPurchase({ ...newPurchase, unitPrice: parseFloat(e.target.value) || 0 })}
                           fullWidth inputProps={{ min: 0 }} />
              </Box>
              {newPurchase.quantityKg > 0 && newPurchase.unitPrice > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <Typography sx={{ fontSize: '13px', color: '#16A34A', fontWeight: 700, fontFamily: 'monospace' }}>
                    Toplam: ${fmt(newPurchase.quantityKg * newPurchase.unitPrice / 1000, 2)}
                  </Typography>
                </Box>
              )}
              <TextField label="Tedarikçi" size="small" value={newPurchase.supplier || ''} onChange={e => setNewPurchase({ ...newPurchase, supplier: e.target.value })} fullWidth />
              <TextField label="Alım Tarihi" type="date" size="small" value={newPurchase.purchasedAt || ''} onChange={e => setNewPurchase({ ...newPurchase, purchasedAt: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Not" size="small" value={newPurchase.notes || ''} onChange={e => setNewPurchase({ ...newPurchase, notes: e.target.value })} fullWidth multiline rows={2} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !newPurchase.quantityKg || !newPurchase.unitPrice}
                  sx={{ bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Kasaya Direkt Ekleme Modalı ── */}
      <Dialog open={addStockOpen} onClose={() => setAddStockOpen(false)} maxWidth="sm" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: '#16A34A', color: 'white', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
          📦 Kasaya Hammadde Ekle
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField select label="Hammadde Tipi" size="small" value={newStock.materialType}
                       onChange={e => setNewStock({ ...newStock, materialType: e.target.value })} fullWidth>
              {MATERIAL_TYPES.map(m => (
                <MenuItem key={m} value={m}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: matColor[m] || '#64748B' }} />{m}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Miktar (kg)" type="number" size="small" value={newStock.quantityKg || ''}
                         onChange={e => setNewStock({ ...newStock, quantityKg: parseFloat(e.target.value) || 0 })}
                         fullWidth inputProps={{ min: 0, step: 100 }} />
              <TextField label="Birim Fiyat ($/ton)" type="number" size="small" value={newStock.unitPrice || ''}
                         onChange={e => setNewStock({ ...newStock, unitPrice: parseFloat(e.target.value) || 0 })}
                         fullWidth inputProps={{ min: 0 }} />
            </Box>
            {newStock.quantityKg > 0 && newStock.unitPrice > 0 && (
              <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <Typography sx={{ fontSize: '13px', color: '#16A34A', fontWeight: 700, fontFamily: 'monospace' }}>
                  Toplam: ${fmt(newStock.quantityKg * newStock.unitPrice / 1000, 2)}
                </Typography>
              </Box>
            )}
            <TextField label="Tedarikçi" size="small" value={newStock.supplier} onChange={e => setNewStock({ ...newStock, supplier: e.target.value })} fullWidth />
            <TextField label="Not" size="small" value={newStock.notes} onChange={e => setNewStock({ ...newStock, notes: e.target.value })} fullWidth multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setAddStockOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleAddStock} disabled={stockSaving || !newStock.quantityKg}
                  sx={{ bgcolor: '#16A34A', borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#15803d', boxShadow: 'none' } }}>
            {stockSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kasaya Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialsPage;
